"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, set, onValue } from "firebase/database"
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage"
import { getAuth } from "firebase/auth"
import { v4 as uuidv4 } from "uuid" // Unique ID generator
import { storage } from "../Firebase/firestore" // Ensure correct Firebase imports
import InfoIcon from "./infoIcon"
import "./productOffer.css" // Import the CSS file

const ProductOffer = ({ adminView = false, selectedBrand = "" }) => {
  const [productName, setProductName] = useState("")
  const [productDetails, setProductDetails] = useState("")
  const [brandName, setBrandName] = useState("")
  const [category, setCategory] = useState("")
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showCatalog, setShowCatalog] = useState(true) // Set to true by default to show catalog
  const [products, setProducts] = useState([])
  const [pendingProducts, setPendingProducts] = useState([])
  const [selectedImage, setSelectedImage] = useState(null)
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200)
  const MAX_RESPONSE_LENGTH = 500

  const categoryOptions = ["Cement", "Steel", "Machinery", "Bulker Cement"]

  // Use selectedBrand from props if in admin view, otherwise use supplierBrand from localStorage
  const supplierBrand = adminView ? selectedBrand : localStorage.getItem("supplierBrand")

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }
  }, [])

  // Fetch products based on the selected brand or all products for admin
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const db = getDatabase()
        const auth = getAuth()
        const user = auth.currentUser

        if (!user && !adminView) {
          console.error("User not authenticated")
          return
        }

        const userId = user ? user.uid : null

        // Fetch approved products from newProducts node
        if (adminView) {
          if (selectedBrand) {
            // Fetch products for the selected brand
            const brandsRef = ref(db, "newProducts")

            onValue(
              brandsRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  const allProducts = []

                  snapshot.forEach((userSnapshot) => {
                    const userData = userSnapshot.val()

                    Object.entries(userData).forEach(([productId, product]) => {
                      if (product.brandName === selectedBrand) {
                        allProducts.push({
                          ...product,
                          userId: userSnapshot.key,
                          productId,
                        })
                      }
                    })
                  })

                  setProducts(allProducts)
                } else {
                  setProducts([])
                }
              },
              { onlyOnce: false }, // Changed to false to keep listening for updates
            )
          } else {
            // Fetch all products for admin view
            const brandsRef = ref(db, "newProducts")

            onValue(
              brandsRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  const allProducts = []

                  snapshot.forEach((userSnapshot) => {
                    const userData = userSnapshot.val()

                    Object.entries(userData).forEach(([productId, product]) => {
                      allProducts.push({
                        ...product,
                        userId: userSnapshot.key,
                        productId,
                      })
                    })
                  })

                  setProducts(allProducts)
                } else {
                  setProducts([])
                }
              },
              { onlyOnce: false }, // Changed to false to keep listening for updates
            )
          }
        } else if (userId) {
          // Fetch approved products for the current supplier from newProducts node
          const userProductsRef = ref(db, `newProducts/${userId}`)

          onValue(
            userProductsRef,
            (snapshot) => {
              if (snapshot.exists()) {
                const productsData = snapshot.val()
                const productsArray = Object.entries(productsData).map(([productId, product]) => ({
                  ...product,
                  productId,
                }))
                console.log("Approved products:", productsArray)
                setProducts(productsArray)
              } else {
                console.log("No approved products found")
                setProducts([])
              }
            },
            { onlyOnce: false }, // Changed to false to keep listening for updates
          )

          // Also fetch pending products for the current supplier from pendingProducts node
          const pendingProductsRef = ref(db, `pendingProducts/${userId}`)

          onValue(
            pendingProductsRef,
            (snapshot) => {
              if (snapshot.exists()) {
                const pendingData = snapshot.val()
                const pendingArray = Object.entries(pendingData).map(([productId, product]) => ({
                  ...product,
                  productId,
                }))
                console.log("Pending products:", pendingArray)
                setPendingProducts(pendingArray)
              } else {
                console.log("No pending products found")
                setPendingProducts([])
              }
            },
            { onlyOnce: false }, // Changed to false to keep listening for updates
          )
        }
      } catch (error) {
        console.error("Error fetching products:", error)
      }
    }

    fetchProducts()
  }, [adminView, selectedBrand, successMessage])

  // Set brand name from selected brand if in admin view
  // useEffect(() => {
  //   if (adminView && selectedBrand) {
  //     setBrandName(selectedBrand)
  //   }
  // }, [adminView, selectedBrand])

  // File selection validation
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]

    if (selectedFile) {
      const fileSize = selectedFile.size / 1024 / 1024 // Convert to MB
      const isPDF = selectedFile.type === "application/pdf"
      const isImage = ["image/png", "image/jpeg", "image/jpg"].includes(selectedFile.type)

      if (isPDF && fileSize > 5) {
        setErrorMessage("File size must be less than 5MB.")
        setFile(null)
        return
      }

      if (isImage && fileSize > 5) {
        setErrorMessage("Image size must be less than 5MB.")
        setFile(null)
        return
      }

      if (!isPDF && !isImage) {
        setErrorMessage("Only PDF, PNG, JPG, and JPEG files are allowed.")
        setFile(null)
        return
      }

      setErrorMessage("")
      setFile(selectedFile)

      // Create preview URL for images
      if (isImage) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setPreviewUrl(reader.result)
        }
        reader.readAsDataURL(selectedFile)
      } else {
        // For PDFs, just show a generic icon
        setPreviewUrl("/Images/pdf-icon.png")
      }
    }
  }

  // Function to validate product data
  const validateProductData = () => {
    setSuccessMessage("")
    setErrorMessage("")

    if (!productName || !brandName || !productDetails || !file || !category) {
      setErrorMessage("Please fill all required fields and select a valid file.")
      return false
    }

    return true
  }

  // Function to show confirmation dialog
  const handleShowConfirmation = () => {
    if (validateProductData()) {
      setShowConfirmation(true)
    }
  }

  // Function to add a new product
  const handleAddProduct = async () => {
    setIsUploading(true)

    try {
      // Firebase Auth check
      const auth = getAuth()
      const user = auth.currentUser

      if (!user) {
        setErrorMessage("User not authenticated! Please log in.")
        setIsUploading(false)
        return
      }

      const userToken = user.uid // Firebase Auth UID

      // Validate file type
      const fileExtension = file.name.split(".").pop().toLowerCase()
      const isImage = ["png", "jpeg", "jpg"].includes(fileExtension)
      const isPDF = fileExtension === "pdf"

      if (!isImage && !isPDF) {
        setErrorMessage("Invalid file format. Please upload an image or a PDF.")
        setIsUploading(false)
        return
      }

      // Upload file to Firebase Storage
      const uniqueFileName = `${uuidv4()}_${file.name}`
      const filePath = `pendingProducts/${userToken}/${uniqueFileName}` // Changed from newProducts to pendingProducts
      const fileRef = storageRef(storage, filePath)
      await uploadBytes(fileRef, file)
      const fileUrl = await getDownloadURL(fileRef)

      // Generate unique product ID
      const offerId = uuidv4()

      // Store product details in Realtime Database under pendingProducts
      const db = getDatabase()
      const product = {
        name: productName,
        brandName,
        category,
        productDetails,
        fileUrl,
        fileType: isImage ? "image" : "pdf",
        timestamp: Date.now(),
        offerId,
        supplierUserId: userToken, // Store the supplier ID for notifications
      }

      const productRef = ref(db, `pendingProducts/${userToken}/${offerId}`) // Changed from newProducts to pendingProducts
      await set(productRef, product)

      setSuccessMessage("Product submitted successfully for apporoval.")
      setProductName("")
      setBrandName("")
      setProductDetails("")
      setFile(null)
      setPreviewUrl(null)
      setShowConfirmation(false)
      setCategory("")

      // Update the pending products list
      setPendingProducts((prev) => [...prev, { ...product, productId: offerId }])
    } catch (error) {
      console.error("Error adding product:", error)
      setErrorMessage("Failed to add product. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  // Function to toggle catalog view
  const toggleCatalog = () => {
    setShowCatalog(!showCatalog)
  }

  // Format date from timestamp
  const formatDate = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString()
  }

  // Function to view product image in larger size
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl)
  }

  // Close image preview
  const closeImagePreview = () => {
    setSelectedImage(null)
  }

  return (
    <div className="product-container">
      <div className="product-header">
        <h1 className="product-title">
          {adminView
            ? `Product Management ${selectedBrand ? `for ${selectedBrand}` : "(All Products)"}`
            : "Product Management"}
          <InfoIcon text="Add and manage products in your catalog. Upload images or PDFs to showcase your products to potential buyers." />
        </h1>
      </div>

      <div className="product-content">
        {/* Product Form Section - Only show if not in admin view or if a brand is selected */}
        {(!adminView || (adminView && selectedBrand)) && (
          <div className="product-form-wrapper">
            <div className="product-form-container">
              <div className="section-header">
                <h2 className="section-title1">
                  Add New Product{" "}
                  <InfoIcon text="Add new products to your catalog. All fields marked with * are required." />
                </h2>
              </div>

              {successMessage && <div className="success-message">{successMessage}</div>}
              {errorMessage && <div className="error-message">{errorMessage}</div>}

              {!showConfirmation && (
                <div className="product-form">
                  <div className="form-group">
                    <label htmlFor="category">
                      Category <span className="required">*</span>
                    </label>
                    <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} required>
                      <option value="" disabled>
                        Select a category
                      </option>
                      {categoryOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="productName">
                      Product Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="productName"
                      placeholder="Enter product name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="brandName">
                      Brand Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="brandName"
                      placeholder="Enter brand name"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productDetails">
                      Product Details (Description & Specification) <span className="required">*</span>
                    </label>
                    <textarea
                      id="productDetails"
                      placeholder="Enter product description and specifications"
                      value={productDetails}
                      onChange={(e) => setProductDetails(e.target.value)}
                      required
                    />
                    <span className="form-hint">
                      Response is required and limited to {MAX_RESPONSE_LENGTH} characters
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="productImage">
                      Product Image/PDF <span className="required">*</span>
                    </label>
                    <input
                      type="file"
                      id="productImage"
                      onChange={handleFileChange}
                      accept="image/png, image/jpeg, image/jpg, application/pdf"
                      required
                    />
                    <small className="form-hint">Accepted formats: PNG, JPG, JPEG, PDF (max 5MB)</small>
                  </div>

                  <button onClick={handleShowConfirmation} disabled={isUploading} className="submit-button">
                    Preview & Confirm
                  </button>
                </div>
              )}

              {/* Confirmation Dialog */}
              {showConfirmation && (
                <div className="confirmation-dialog">
                  <h3>Confirm Product Details</h3>
                  <div className="confirmation-content">
                    <div className="product-preview">
                      {previewUrl && (
                        <div className="preview-image">
                          <img
                            src={previewUrl || "/placeholder.svg"}
                            alt="Product Preview"
                            className="preview-image-img"
                          />
                        </div>
                      )}
                      <div className="product-details">
                        <h4>{productName}</h4>
                        <p className="brand">
                          <strong>Brand:</strong> {brandName}
                        </p>
                        <p className="category">
                          <strong>Category:</strong> {category}
                        </p>
                        <div className="details">
                          <strong>Product Details:</strong>
                          <p>{productDetails}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="confirmation-actions">
                    <button onClick={() => setShowConfirmation(false)} className="edit-button">
                      Edit
                    </button>
                    <button onClick={handleAddProduct} disabled={isUploading} className="confirm-button">
                      {isUploading ? "Uploading..." : "Submit for Approval"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product Catalog Section - Full width when no form is shown */}
        <div className={`product-catalog-wrapper ${!adminView || (adminView && selectedBrand) ? "" : "full-width"}`}>
          <div className="product-catalog-container">
            <div className="section-header">
              <h2 className="section-product-title">
                Product Catalog <InfoIcon text="View all your added products in one place." />
              </h2>
              <button onClick={toggleCatalog} className="toggle-button">
                {showCatalog ? "Hide Catalog" : "Show Catalog"}
              </button>
            </div>

            {showCatalog && (
              <div className="product-catalog">
                {!adminView && pendingProducts.length > 0 && (
                  <div className="pending-products-section">
                    <h3 className="section-subtitle">Pending Approval</h3>
                    <div className="catalog-grid">
                      {pendingProducts.map((product) => (
                        <div key={product.offerId || product.productId} className="product-card pending">
                          <div className="pending-badge">Pending Approval</div>
                          <div
                            className="product-image"
                            onClick={() => product.fileType === "image" && handleImageClick(product.fileUrl)}
                          >
                            {product.fileType === "image" ? (
                              <img
                                src={product.fileUrl || "/placeholder.svg"}
                                alt={product.name}
                                className="clickable-image"
                              />
                            ) : (
                              <div className="pdf-icon">PDF</div>
                            )}
                          </div>
                          <div className="product-info">
                            <h3>{product.name}</h3>
                            <p className="brand-name">{product.brandName || product.companyName}</p>
                            <p className="date">Submitted: {formatDate(product.timestamp)}</p>
                            <p className="status-pending">Under Approval</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="approved-products-section">
                  <h3 className="section-subtitle">
                    Approved Products
                  </h3>
                  {products.length === 0 ? (
                    <div className="no-products">
                      <p>No approved products yet.</p>
                      {!adminView ? (
                        <p>Submit products for approval using the form above.</p>
                      ) : (
                        <p>Select a brand to view products.</p>
                      )}
                    </div>
                  ) : (
                    <div className="catalog-grid">
                      {products.map((product) => (
                        <div key={product.offerId || product.productId} className="product-card">
                          <div
                            className="product-image"
                            onClick={() => product.fileType === "image" && handleImageClick(product.fileUrl)}
                          >
                            {product.fileType === "image" ? (
                              <img
                                src={product.fileUrl || "/placeholder.svg"}
                                alt={product.name}
                                className="clickable-image"
                              />
                            ) : (
                              <div className="pdf-icon">PDF</div>
                            )}
                          </div>
                          <div className="product-info">
                            <h3>{product.name}</h3>
                            <p className="brand-name">{product.brandName || product.companyName}</p>
                            <p className="date">Added: {formatDate(product.timestamp)}</p>
                            {product.approvalDate && (
                              <p className="date">Approved: {formatDate(product.approvalDate)}</p>
                            )}
                            <p className="status-approved">Approved</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="image-preview-modal" onClick={closeImagePreview}>
          <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-preview" onClick={closeImagePreview}>
              &times;
            </span>
            <img src={selectedImage || "/placeholder.svg"} alt="Product Preview" />
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductOffer;
